// src/pages/personal/settings/PromotionsDiscountsSection/PromoCodes.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import { TEXT_STYLES } from '@/theme/typography';
import { COMPONENT_STYLES } from '@/theme/components';
import { COLORS } from '@/theme/colors';
import useBusinessCurrency from '@/hooks/useBusinessCurrency';
import { PromoCode } from './types';
import { Card, Flex, Grid } from '@/components/ui/Layout';
import { Table, TableColumn } from '@/components/ui/Table';
import { useResponsive } from '@/hooks/useResponsive';

interface PromoCodesProps {
  promoCodes: {
    [code: string]: PromoCode;
  };
  onChange: (data: any) => void;
}

interface PromoCodeFormData {
  code: string;
  description: string;
  startDate: string;
  endDate: string;
  active: boolean;
  recurring: boolean;
  interval: 'weekly' | 'monthly' | 'yearly';
  type: 'percentage' | 'fixed_amount';
  value: number;
}

// Тип для строки данных таблицы промокодов
interface PromoCodeTableRow extends PromoCode {
  code: string;
}

const initialFormData: PromoCodeFormData = {
  code: '',
  description: '',
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  active: true,
  recurring: false,
  interval: 'monthly',
  type: 'percentage',
  value: 0
};

const PromoCodes: React.FC<PromoCodesProps> = ({ promoCodes, onChange }) => {
  // ИСПРАВЛЕНО: Указываем используемые пространства имен
  const { t } = useTranslation(['promotions', 'common']);
  const { currencySymbol } = useBusinessCurrency();
  const [isAdding, setIsAdding] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [formData, setFormData] = useState<PromoCodeFormData>(initialFormData);
  const { isMobile } = useResponsive();
  
  // Преобразуем промокоды в массив для отображения
  const promoCodesList: PromoCodeTableRow[] = Object.entries(promoCodes).map(([code, data]) => ({
    code,
    ...data
  }));

  // Обработчик изменения поля формы
  const handleFormChange = (field: keyof PromoCodeFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Открытие формы добавления нового промокода
  const handleAddNew = () => {
    setFormData(initialFormData);
    setIsAdding(true);
    setEditingCode(null);
  };

  // Открытие формы редактирования существующего промокода
  const handleEdit = (code: string) => {
    const promoData = promoCodes[code];
    
    setFormData({
      code,
      description: promoData.description,
      startDate: promoData.period[0].split('T')[0],
      endDate: promoData.period[1].split('T')[0],
      active: promoData.active,
      recurring: promoData.recurrence?.recurring || false,
      interval: promoData.recurrence?.interval || 'monthly',
      type: promoData.type,
      value: promoData.value
    });
    
    setEditingCode(code);
    setIsAdding(true);
  };

  // Удаление промокода
  const handleDelete = (code: string) => {
    const updatedPromoCodes = { ...promoCodes };
    delete updatedPromoCodes[code];
    onChange(updatedPromoCodes);
  };

  // Сохранение промокода
  const handleSave = () => {
    if (!formData.code.trim()) {
      // Используем confirm вместо alert для браузерной среды
      window.confirm(t('promotions:promotions.code_required'));
      return;
    }

    const newPromoCode: PromoCode = {
      description: formData.description,
      period: [
        `${formData.startDate}T00:00:00Z`,
        `${formData.endDate}T23:59:59Z`
      ],
      active: formData.active,
      recurrence: formData.recurring ? {
        recurring: true,
        interval: formData.interval
      } : undefined,
      type: formData.type,
      value: formData.value
    };

    const updatedPromoCodes = { ...promoCodes };
    
    if (editingCode && editingCode !== formData.code) {
      // Если код был изменен, удаляем старый и добавляем новый
      delete updatedPromoCodes[editingCode];
    }
    
    updatedPromoCodes[formData.code] = newPromoCode;
    onChange(updatedPromoCodes);
    
    // Сбрасываем состояние формы
    setIsAdding(false);
    setEditingCode(null);
    setFormData(initialFormData);
  };

  // Отмена добавления/редактирования
  const handleCancel = () => {
    setIsAdding(false);
    setEditingCode(null);
    setFormData(initialFormData);
  };

  // Определяем колонки для таблицы промокодов
  const columns: TableColumn<PromoCodeTableRow>[] = [
    {
      key: 'code',
      title: t('promotions:promotions.code'),
      width: 'md',
      render: (item) => <span className="font-medium">{item.code}</span>
    },
    {
      key: 'description',
      title: t('promotions:promotions.description'),
      width: 'md'
    },
    {
      key: 'period',
      title: t('promotions:promotions.period'),
      width: 'lg',
      render: (item) => (
        <div>
          {`${new Date(item.period[0]).toLocaleDateString()} - ${new Date(item.period[1]).toLocaleDateString()}`}
          {item.recurrence?.recurring && (
            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              {t(`promotions:promotions.${item.recurrence.interval}`)}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'discount',
      title: t('promotions:promotions.discount'),
      width: 'md',
      render: (item) => (
        <span>{item.value} {item.type === 'percentage' ? '%' : currencySymbol}</span>
      )
    },
    {
      key: 'status',
      title: t('promotions:promotions.status'),
      width: 'md',
      render: (item) => (
        <span className={`px-2 py-1 rounded text-xs ${item.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {item.active ? t('promotions:promotions.active') : t('promotions:promotions.inactive')}
        </span>
      )
    },
    {
      key: 'actions',
      title: t('common:actions'),
      width: 'sm',
      textAlign: 'right',
      render: (item) => (
        <Flex justify="end" gap="xs">
          <button
            onClick={() => handleEdit(item.code)}
            className="text-blue-600 hover:text-blue-800"
            title={t('common:edit')}
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(item.code)}
            className="text-red-600 hover:text-red-800"
            title={t('common:delete')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </Flex>
      )
    }
  ];

  return (
    <Card 
      padding={isMobile ? "sm" : "md"} 
      className="border border-gray-200"
    >
      <Flex justify="between" align="center" className="mb-4">
        <Flex align="center" gap="xs">
          <Tag className={`w-5 h-5 text-primary`} />
          <h3 className={TEXT_STYLES.h3}>{t('promotions:promotions.promo_codes')}</h3>
        </Flex>
        
        {!isAdding && (
          <button
            onClick={handleAddNew}
            className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.sm} ${COMPONENT_STYLES.button.secondary}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('common:add')}
          </button>
        )}
      </Flex>
      
      <p className={`mb-4 text-sm text-[${COLORS.secondary}]`}>
        {t('promotions:promotions.promo_codes_description')}
      </p>
      
      {/* Форма добавления/редактирования промокода */}
      {isAdding && (
        <Card 
          padding="md" 
          className="bg-gray-50 mb-4 border border-gray-200"
        >
          <h3 className={`${TEXT_STYLES.h3} mb-3`}>
            {editingCode ? t('promotions:promotions.edit_promo_code') : t('promotions:promotions.add_promo_code')}
          </h3>
          
          <Grid 
            cols={isMobile ? 1 : 2} 
            gap="md" 
            className="mb-4"
            mobileCols={1}
          >
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.code')} *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value)}
                className={COMPONENT_STYLES.form.input}
                placeholder={t('promotions:promotions.code_placeholder')}
                required
              />
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.description')}
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                className={COMPONENT_STYLES.form.input}
                placeholder={t('promotions:promotions.description_placeholder')}
              />
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.start_date')}
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleFormChange('startDate', e.target.value)}
                className={COMPONENT_STYLES.form.input}
              />
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.end_date')}
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleFormChange('endDate', e.target.value)}
                className={COMPONENT_STYLES.form.input}
              />
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.discount_type')}
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleFormChange('type', e.target.value)}
                className={COMPONENT_STYLES.form.input}
              >
                <option value="percentage">{t('promotions:promotions.percentage')}</option>
                <option value="fixed_amount">{t('promotions:promotions.fixed_amount')}</option>
              </select>
            </div>
            
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.discount_value')}
              </label>
              <Flex align="center">
                <input
                  type="number"
                  min="0"
                  value={formData.value}
                  onChange={(e) => handleFormChange('value', parseFloat(e.target.value))}
                  className={COMPONENT_STYLES.form.input}
                />
                <span className="ml-2">
                  {formData.type === 'percentage' ? '%' : currencySymbol}
                </span>
              </Flex>
            </div>
          </Grid>
          
          <Grid 
            cols={isMobile ? 1 : 2} 
            gap="md" 
            className="mb-4"
            mobileCols={1}
          >
            <div className={COMPONENT_STYLES.form.checkbox.container}>
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFormChange('active', e.currentTarget.checked);
                }}
                className={COMPONENT_STYLES.form.checkbox.input}
              />
              <label htmlFor="active" className={COMPONENT_STYLES.form.checkbox.label}>
                {t('promotions:promotions.active')}
              </label>
            </div>
            
            <div className={COMPONENT_STYLES.form.checkbox.container}>
              <input
                type="checkbox"
                id="recurring"
                checked={formData.recurring}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFormChange('recurring', e.currentTarget.checked);
                }}
                className={COMPONENT_STYLES.form.checkbox.input}
              />
              <label htmlFor="recurring" className={COMPONENT_STYLES.form.checkbox.label}>
                {t('promotions:promotions.recurring')}
              </label>
            </div>
          </Grid>
          
          {formData.recurring && (
            <div className="mb-4">
              <label className={COMPONENT_STYLES.form.label}>
                {t('promotions:promotions.interval')}
              </label>
              <select
                value={formData.interval}
                onChange={(e) => handleFormChange('interval', e.target.value as 'weekly' | 'monthly' | 'yearly')}
                className={COMPONENT_STYLES.form.input}
              >
                <option value="weekly">{t('promotions:promotions.weekly')}</option>
                <option value="monthly">{t('promotions:promotions.monthly')}</option>
                <option value="yearly">{t('promotions:promotions.yearly')}</option>
              </select>
            </div>
          )}
          
          <Flex justify="end" gap="xs">
            <button
              onClick={handleCancel}
              className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.sm} ${COMPONENT_STYLES.button.outline}`}
            >
              <X className="w-4 h-4 mr-1" />
              {t('common:cancel')}
            </button>
            
            <button
              onClick={handleSave}
              className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.sm} ${COMPONENT_STYLES.button.primary}`}
            >
              <Check className="w-4 h-4 mr-1" />
              {t('common:save')}
            </button>
          </Flex>
        </Card>
      )}
      
      {/* Список промокодов */}
      {promoCodesList.length > 0 ? (
        <Table
          data={promoCodesList}
          columns={columns}
          scrollable={true}
          keyExtractor={(item) => item.code}
          compactMode={isMobile}
          mobileBehavior={isMobile ? "scroll" : "table"}
        />
      ) : (
        <Card padding="md" className="text-center bg-gray-50">
          <p className="text-gray-500">{t('promotions:promotions.no_promo_codes')}</p>
          {!isAdding && (
            <button
              onClick={handleAddNew}
              className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.sm} ${COMPONENT_STYLES.button.secondary} mt-2`}
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('promotions:promotions.add_first_promo_code')}
            </button>
          )}
        </Card>
      )}
    </Card>
  );
};

export default PromoCodes;